import { createBrowserRouter, RouterProvider, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, Suspense, lazy, useContext } from "react";
import { RevealContext } from './context/RevealContext';
import { ReactLenis } from 'lenis/react';

import LoadingScreen from './loading-transition/LoadingScreen.jsx';
import Transition from './loading-transition/Transition.jsx';
import MainMenu from "./menu-link/MainMenu.jsx";
import FooterMenu from "./footer/FooterMenu.jsx";
import Home from "./pages/Home.jsx";
import Projects from "./pages/Projects.jsx";
import About from "./pages/About.jsx";
import Contact from "./pages/Contact.jsx";
import SingleProject from "./pages/SingleProject.jsx";
import PageNotFound from "./pages/PageNotFound.jsx";
import { RevealProvider } from './context/RevealContext';

function RouteReveal() {
    const { resetReveal } = useContext(RevealContext);
    const location = useLocation();
    useEffect(() => {
        const timer = setTimeout(() => resetReveal(), 350);
        return () => clearTimeout(timer);
    }, [location.pathname]);
    return null;
}
import { getLang } from '../lang';
import ScrollBar from "./background/ScrollBar.jsx";
import Cursor from "./background/Cursor.jsx";
import initMagnetic from '../utils/initMagnetic'
import { sendPageView, hasAnalyticsConsent, enableAnalytics } from '../utils/analytics'
import SitePreferences from './SitePreferences.jsx';

const ThreeCanvas = lazy(() => import('./background/ThreeCanvas.jsx'));

function App() {
    // Loading
    const [started, setStarted] = useState(false);
    const [progress, setProgress] = useState(0);

    // Lang
    const [lang, setLang] = useState(getLang());

    // Routes
    const location = useLocation();
    const navigate = useNavigate();

    // Performances
    const [lowPerformance, setIsLowPerformance] = useState(false);
    const [lowPerfContent, setIslowPerfContent] = useState(false);


    // Menus
    const footerMenu = useMemo(() => <FooterMenu navigate={navigate} lang={lang} />, [lang]);

    // Routes
    let content;
    switch (location.pathname) {
        case '/':
            content = <Home position={[0, 0, 0]} navigate={navigate} lowPerfContent={lowPerfContent} lang={lang} />;
            break;
        case '/projects':
            content = <Projects navigate={navigate} lang={lang} />;
            break;
        case '/about':
            content = <About lang={lang} />;
            break;
        case '/contact':
            content = <Contact lang={lang} />;
            break;
        case location.pathname.match(/^\/projects\/.+$/)?.input:
            content = <SingleProject navigate={navigate} lang={lang} />;
            break;
        default:
            content = <PageNotFound />;
            break;
    }


    // Complète la loading screen indépendamment de Three.js.
    // ThreeCanvas ne monte qu'après started=true, ce qui sort vendor-three
    // de la fenêtre TBT mesurée par Lighthouse.
    useEffect(() => {
        setProgress(100);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            const newLang = e?.detail ?? (e?.key === 'lang' ? e.newValue : null);
            if (newLang) setLang(newLang);
        };
        window.addEventListener('langchange', handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('langchange', handler);
            window.removeEventListener('storage', handler);
        };
    }, []);

    useEffect(() => {
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
    }, [lang]);

    // Init global magnetic for all .button elements
    useEffect(() => {
        const controller = initMagnetic({ selector: '.button', childSelector: '.round-element', maxDist: 10 })
        return () => controller.destroy()
    }, [])


    useEffect(() => {
        if (hasAnalyticsConsent() === true) {
            enableAnalytics();
        }

        const onStorage = (e) => {
            if (e.key === 'analytics_consent') {
                if (e.newValue === 'true') enableAnalytics();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        // send page_view on route change
        if (hasAnalyticsConsent()) {
            sendPageView(location.pathname, document.title);
        }
    }, [location.pathname]);

    return (
        <>
            {/* LoadingScreen et transitions de page */}
            <LoadingScreen started={started} progress={progress} setStarted={setStarted} />
            <Transition />


            {/*Lenis*/}
            <ReactLenis
                className="container"
                options={{
                    duration: 1.8,
                    easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
                    direction: 'vertical',
                    smooth: true,
                    smoothTouch: false,
                    touchMultiplier: 3
                }}
            >
<ScrollBar/>
                <Cursor/>
                <SitePreferences lang={lang} />
                {/*HTML content*/}
                <RevealProvider lang={lang} started={started}>
                    <RouteReveal />
                    <div className={`container-inner lang-${lang}`} style={{ zIndex: 3, position: "relative" }}>
                        <MainMenu navigate={navigate} setLang={setLang} lang={lang} />
                        <div className="main-content">
                            {content}
                            {footerMenu}
                        </div>
                    </div>
                </RevealProvider>

                {/*ThreeJS — monté seulement après la loading screen pour sortir vendor-three de la fenêtre TBT*/}
                {started && (
                    <Suspense fallback={null}>
                        <ThreeCanvas
                            setIsLowPerformance={setIsLowPerformance}
                            setIslowPerfContent={setIslowPerfContent}
                            lowPerformance={lowPerformance}
                        />
                    </Suspense>
                )}
            </ReactLenis>
        </>
    );
}

// Create router at module scope so future flags are applied once at import time
const router = createBrowserRouter([
    { path: "/*", element: <App /> }
], {
    future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true
    }
});

function AppWrapper() {
    return (
        <RouterProvider router={router} />
    );
}

export default AppWrapper;
